/**
 * ConflictResolver - Sistema de detecção e resolução de conflitos 3-way
 *
 * Implementa detecção completa de conflitos comparando:
 * 1. Base (última versão sincronizada)
 * 2. Local (mudanças do usuário)
 * 3. Server (mudanças do servidor)
 *
 * Estratégias de resolução:
 * - SERVER_WINS: Servidor sempre ganha (padrão atual)
 * - LOCAL_WINS: Local sempre ganha
 * - LAST_WRITE_WINS: Mais recente ganha
 * - MANUAL: Requer intervenção do usuário (via admin)
 * - MERGE: Tenta merge automático de campos não conflitantes
 */

import { recordError, recordEvent } from "../observability/events";
import { syncLogger } from "./SyncLogger";

/**
 * Estratégias de resolução de conflitos
 */
export const ConflictStrategy = {
  SERVER_WINS: "SERVER_WINS",
  LOCAL_WINS: "LOCAL_WINS",
  LAST_WRITE_WINS: "LAST_WRITE_WINS",
  MANUAL: "MANUAL",
  MERGE: "MERGE",
};

/**
 * Tipos de conflitos detectados
 */
export const ConflictType = {
  // Ambos modificaram o mesmo registro
  UPDATE_UPDATE: "UPDATE_UPDATE",
  // Local deletou, servidor atualizou
  DELETE_UPDATE: "DELETE_UPDATE",
  // Local atualizou, servidor deletou
  UPDATE_DELETE: "UPDATE_DELETE",
  // Ambos criaram registro com mesmo ID (raro, mas possível)
  CREATE_CREATE: "CREATE_CREATE",
};

/**
 * Campos que devem ser ignorados na comparação
 */
const IGNORED_FIELDS = [
  "_status",
  "_changed",
  "syncStatus",
  "updated_at",
  "updatedAt",
  "created_at",
  "createdAt",
  "last_synced_at",
];

/**
 * Campos que podem ser merged automaticamente (não críticos)
 */
const MERGEABLE_FIELDS = [
  "observacao",
  "observacoes",
  "observation",
  "notes",
  "comments",
];

/**
 * Representa um conflito detectado
 */
export class Conflict {
  constructor(entityType, recordId, type, localData, serverData, baseData = null) {
    this.id = `${entityType}_${recordId}_${Date.now()}`;
    this.entityType = entityType;
    this.recordId = recordId;
    this.type = type;
    this.localData = localData;
    this.serverData = serverData;
    this.baseData = baseData;
    this.detectedAt = Date.now();
    this.resolvedAt = null;
    this.resolution = null;
    this.resolvedBy = null; // 'auto' ou userId
    this.conflictingFields = [];
  }

  /**
   * Resolve o conflito com uma estratégia
   */
  resolve(strategy, resolvedBy = "auto") {
    this.resolution = strategy;
    this.resolvedAt = Date.now();
    this.resolvedBy = resolvedBy;
    return this;
  }

  /**
   * Obtém os dados resultantes baseado na resolução
   */
  getResolvedData() {
    switch (this.resolution) {
      case ConflictStrategy.SERVER_WINS:
        return this.serverData;
      case ConflictStrategy.LOCAL_WINS:
        return this.localData;
      case ConflictStrategy.LAST_WRITE_WINS:
        return this._getLastWriteData();
      case ConflictStrategy.MERGE:
        return this._getMergedData();
      default:
        return this.serverData; // Default seguro
    }
  }

  _getLastWriteData() {
    const localUpdatedAt = this._getUpdatedAt(this.localData);
    const serverUpdatedAt = this._getUpdatedAt(this.serverData);
    return localUpdatedAt > serverUpdatedAt ? this.localData : this.serverData;
  }

  _getUpdatedAt(data) {
    const raw = data?._raw || data;
    const updatedAt = raw?.updated_at || raw?.updatedAt;
    if (!updatedAt) return 0;
    return typeof updatedAt === "number" ? updatedAt : new Date(updatedAt).getTime();
  }

  _getMergedData() {
    // Começa com dados do servidor
    const merged = { ...this.serverData };

    // Para cada campo conflitante, tenta merge inteligente
    for (const field of this.conflictingFields) {
      if (MERGEABLE_FIELDS.includes(field)) {
        // Campos mergeable: concatenar se diferentes
        const localValue = this.localData[field];
        const serverValue = this.serverData[field];

        if (localValue && serverValue && localValue !== serverValue) {
          merged[field] = `${serverValue}\n---\n[Alteração local]: ${localValue}`;
        } else {
          merged[field] = localValue || serverValue;
        }
      } else {
        // Campos não mergeable: usa LAST_WRITE_WINS
        const localUpdatedAt = this._getUpdatedAt(this.localData);
        const serverUpdatedAt = this._getUpdatedAt(this.serverData);
        merged[field] =
          localUpdatedAt > serverUpdatedAt
            ? this.localData[field]
            : this.serverData[field];
      }
    }

    return merged;
  }
}

/**
 * Classe principal de resolução de conflitos
 */
class ConflictResolver {
  constructor() {
    // Estratégia padrão por tipo de entidade
    this.defaultStrategies = {
      // Entidades críticas: servidor ganha
      farms: ConflictStrategy.SERVER_WINS,
      corrals: ConflictStrategy.SERVER_WINS,
      batches: ConflictStrategy.SERVER_WINS,
      protocols: ConflictStrategy.SERVER_WINS,
      users: ConflictStrategy.SERVER_WINS,

      // Entidades de trabalho de campo: mais recente ganha
      d0s: ConflictStrategy.LAST_WRITE_WINS,
      dns: ConflictStrategy.LAST_WRITE_WINS,
      iatfs: ConflictStrategy.LAST_WRITE_WINS,

      // Touros e partidas: servidor ganha (dados de referência)
      bulls_new: ConflictStrategy.SERVER_WINS,
      partida: ConflictStrategy.SERVER_WINS,

      // Domains: servidor sempre ganha
      domain_types: ConflictStrategy.SERVER_WINS,
      domain_values: ConflictStrategy.SERVER_WINS,
    };

    // Conflitos pendentes de resolução manual
    this.pendingConflicts = [];

    // Histórico de conflitos resolvidos
    this.resolvedConflicts = [];

    // Carregar estado persistido
    this._loadState();
  }

  /**
   * Detecta conflito entre dados locais e do servidor
   *
   * @param {string} entityType - Tipo da entidade (nome da tabela)
   * @param {Object} localRecord - Registro local (WatermelonDB record)
   * @param {Object} serverData - Dados do servidor
   * @param {Object} baseData - Dados base (última versão sincronizada, opcional)
   * @returns {Conflict|null} Conflito detectado ou null
   */
  detectConflict(entityType, localRecord, serverData, baseData = null) {
    const localRaw = localRecord?._raw || localRecord;

    // Verificar se local tem mudanças não sincronizadas
    if (localRaw._status === "synced") {
      // Local não tem mudanças - não há conflito
      return null;
    }

    // Detectar tipo de conflito
    const type = this._detectConflictType(localRaw, serverData);

    if (!type) {
      return null;
    }

    // Criar objeto de conflito
    const conflict = new Conflict(
      entityType,
      localRaw.id,
      type,
      localRaw,
      serverData,
      baseData
    );

    // Identificar campos conflitantes
    conflict.conflictingFields = this._findConflictingFields(
      localRaw,
      serverData,
      baseData
    );

    // Log do conflito detectado
    syncLogger.logConflict(entityType, localRaw.id, localRaw, serverData);

    return conflict;
  }

  /**
   * Resolve um conflito usando a estratégia configurada
   *
   * @param {Conflict} conflict - Conflito a resolver
   * @param {string} strategy - Estratégia de resolução (opcional, usa padrão se não informado)
   * @returns {Object} Dados resolvidos
   */
  resolveConflict(conflict, strategy = null) {
    const resolveStrategy =
      strategy || this.defaultStrategies[conflict.entityType] || ConflictStrategy.SERVER_WINS;

    // Se estratégia é MANUAL, adicionar à lista de pendentes
    if (resolveStrategy === ConflictStrategy.MANUAL) {
      this.pendingConflicts.push(conflict);
      this._saveState();

      syncLogger.log("CONFLICT_PENDING_MANUAL", {
        id: conflict.id,
        entityType: conflict.entityType,
        recordId: conflict.recordId,
      });

      // Retorna dados do servidor como fallback seguro
      return conflict.serverData;
    }

    // Resolver automaticamente
    conflict.resolve(resolveStrategy, "auto");

    // Mover para histórico
    this.resolvedConflicts.push(conflict);
    this._trimHistory();
    this._saveState();

    syncLogger.log("CONFLICT_RESOLVED", {
      id: conflict.id,
      entityType: conflict.entityType,
      recordId: conflict.recordId,
      strategy: resolveStrategy,
      conflictingFields: conflict.conflictingFields,
    });

    recordEvent({
      level: "warn",
      category: "sync",
      action: "conflict_resolved",
      payload: {
        entityType: conflict.entityType,
        recordId: conflict.recordId,
        strategy: resolveStrategy,
        conflictingFields: conflict.conflictingFields,
      },
    });

    return conflict.getResolvedData();
  }

  /**
   * Resolve conflito manualmente (chamado pelo admin)
   *
   * @param {string} conflictId - ID do conflito
   * @param {string} strategy - Estratégia escolhida pelo admin
   * @param {string} userId - ID do admin que resolveu
   * @returns {Object|null} Dados resolvidos ou null se conflito não encontrado
   */
  resolveManually(conflictId, strategy, userId) {
    const index = this.pendingConflicts.findIndex((c) => c.id === conflictId);

    if (index === -1) {
      return null;
    }

    const conflict = this.pendingConflicts[index];
    conflict.resolve(strategy, userId);

    // Remover dos pendentes
    this.pendingConflicts.splice(index, 1);

    // Adicionar ao histórico
    this.resolvedConflicts.push(conflict);
    this._trimHistory();
    this._saveState();

    syncLogger.log("CONFLICT_RESOLVED_MANUALLY", {
      id: conflict.id,
      entityType: conflict.entityType,
      recordId: conflict.recordId,
      strategy,
      resolvedBy: userId,
    });

    return conflict.getResolvedData();
  }

  /**
   * Obtém conflitos pendentes de resolução manual
   */
  getPendingConflicts() {
    return [...this.pendingConflicts];
  }

  /**
   * Obtém histórico de conflitos resolvidos
   */
  getResolvedConflicts(limit = 100) {
    return this.resolvedConflicts.slice(-limit);
  }

  /**
   * Obtém estatísticas de conflitos
   */
  getStats() {
    const byType = {};
    const byEntity = {};

    [...this.pendingConflicts, ...this.resolvedConflicts].forEach((conflict) => {
      byType[conflict.type] = (byType[conflict.type] || 0) + 1;
      byEntity[conflict.entityType] = (byEntity[conflict.entityType] || 0) + 1;
    });

    return {
      pending: this.pendingConflicts.length,
      resolved: this.resolvedConflicts.length,
      byType,
      byEntity,
    };
  }

  /**
   * Configura estratégia padrão para uma entidade
   */
  setDefaultStrategy(entityType, strategy) {
    this.defaultStrategies[entityType] = strategy;
    this._saveState();
  }

  /**
   * Limpa todos os conflitos pendentes (resolvendo com SERVER_WINS)
   */
  clearPending() {
    for (const conflict of this.pendingConflicts) {
      conflict.resolve(ConflictStrategy.SERVER_WINS, "system_clear");
      this.resolvedConflicts.push(conflict);
    }
    this.pendingConflicts = [];
    this._trimHistory();
    this._saveState();
  }

  // =====================
  // Métodos privados
  // =====================

  _detectConflictType(localRaw, serverData) {
    const localIsDeleted = localRaw.isDeleted || localRaw.is_deleted;
    const serverIsDeleted = serverData.isDeleted || serverData.is_deleted;
    const localStatus = localRaw._status;

    // UPDATE_UPDATE: ambos modificaram
    if (!localIsDeleted && !serverIsDeleted && localStatus !== "synced") {
      return ConflictType.UPDATE_UPDATE;
    }

    // DELETE_UPDATE: local deletou, servidor atualizou
    if (localIsDeleted && !serverIsDeleted) {
      return ConflictType.DELETE_UPDATE;
    }

    // UPDATE_DELETE: local atualizou, servidor deletou
    if (!localIsDeleted && serverIsDeleted && localStatus !== "synced") {
      return ConflictType.UPDATE_DELETE;
    }

    return null;
  }

  _findConflictingFields(localRaw, serverData, baseData) {
    const conflicting = [];

    // Obter todos os campos do servidor
    const serverFields = Object.keys(serverData).filter(
      (f) => !IGNORED_FIELDS.includes(f) && !f.startsWith("_")
    );

    for (const field of serverFields) {
      // Converter nome do campo para formato local (snake_case)
      const localField = this._toSnakeCase(field);
      const localValue = localRaw[localField] ?? localRaw[field];
      const serverValue = serverData[field];

      // Se temos base, comparar com base para 3-way
      if (baseData) {
        const baseValue = baseData[field] ?? baseData[localField];

        // Conflito real: local e servidor mudaram de formas diferentes
        if (
          !this._isEqual(localValue, baseValue) &&
          !this._isEqual(serverValue, baseValue) &&
          !this._isEqual(localValue, serverValue)
        ) {
          conflicting.push(field);
        }
      } else {
        // Sem base: comparar diretamente
        if (!this._isEqual(localValue, serverValue)) {
          conflicting.push(field);
        }
      }
    }

    return conflicting;
  }

  _toSnakeCase(str) {
    return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
  }

  _isEqual(a, b) {
    // Null/undefined check
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;

    // Date comparison
    if (a instanceof Date && b instanceof Date) {
      return a.getTime() === b.getTime();
    }

    // String date comparison
    if (typeof a === "string" && typeof b === "string") {
      const dateA = Date.parse(a);
      const dateB = Date.parse(b);
      if (!isNaN(dateA) && !isNaN(dateB)) {
        return dateA === dateB;
      }
    }

    // Primitive comparison
    return a === b;
  }

  _trimHistory() {
    const MAX_HISTORY = 500;
    if (this.resolvedConflicts.length > MAX_HISTORY) {
      this.resolvedConflicts = this.resolvedConflicts.slice(-MAX_HISTORY);
    }
  }

  _loadState() {
    try {
      const stored = localStorage.getItem("conflict_resolver_state");
      if (stored) {
        const state = JSON.parse(stored);
        this.pendingConflicts = state.pendingConflicts || [];
        this.resolvedConflicts = state.resolvedConflicts || [];
        if (state.defaultStrategies) {
          this.defaultStrategies = {
            ...this.defaultStrategies,
            ...state.defaultStrategies,
          };
        }
      }
    } catch (e) {
      console.error("[ConflictResolver] failed to load state", e);
      recordError(e instanceof Error ? e : new Error(String(e)), {
        category: "sync",
        action: "conflict_resolver_load_state_failed",
      });
    }
  }

  _saveState() {
    try {
      const state = {
        pendingConflicts: this.pendingConflicts,
        resolvedConflicts: this.resolvedConflicts,
        defaultStrategies: this.defaultStrategies,
      };
      localStorage.setItem("conflict_resolver_state", JSON.stringify(state));
    } catch (e) {
      console.error("[ConflictResolver] failed to save state", e);
      recordError(e instanceof Error ? e : new Error(String(e)), {
        category: "sync",
        action: "conflict_resolver_save_state_failed",
      });
    }
  }
}

// Singleton
export const conflictResolver = new ConflictResolver();

export default ConflictResolver;
